import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PlanBadge } from '@/components/PlanBadge';
import { NotificationBell } from '@/components/NotificationBell';
import { getPlanMeta } from '@/lib/plans';
import {
  Heart, MessageCircle, User, LogOut, Sparkles, Calendar, Shield, ConciergeBell, Menu,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

export const AppNav = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscription, setSubscription] = useState(null);
  const [concierge, setConcierge] = useState({ unread_messages: 0, open_requests: 0 });
  const [open, setOpen] = useState(false);

  const fetchMeta = useCallback(async () => {
    try {
      const [sub, con] = await Promise.allSettled([
        axios.get(`${API}/payments/subscription`, authHeaders()),
        axios.get(`${API}/concierge/unread`, authHeaders()),
      ]);
      if (sub.status === 'fulfilled') setSubscription(sub.value.data);
      if (con.status === 'fulfilled') setConcierge(con.value.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    window.location.href = '/';
  };

  const conciergeBadge = concierge.unread_messages || concierge.open_requests || 0;
  const canBrowse = getPlanMeta(subscription?.plan_type).canBrowse;

  const links = [
    { to: '/dashboard', label: 'Home', icon: Heart },
    { to: '/matches', label: 'My Matches', icon: Heart },
    { to: '/messages', label: 'Messages', icon: MessageCircle },
    { to: '/reflections', label: 'Reflections', icon: Calendar },
    canBrowse && { to: '/browse', label: 'Browse All', icon: Sparkles },
    { to: '/concierge', label: 'Concierge', icon: ConciergeBell, badge: conciergeBadge },
    { to: '/profile', label: 'Profile', icon: User },
    user?.is_staff && { to: '/admin', label: user?.role === 'live_host' ? 'Hosting' : 'Admin', icon: Shield },
  ].filter(Boolean);

  const isActive = (to) => location.pathname === to || (to !== '/dashboard' && location.pathname.startsWith(to));

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-rose-100 shadow-sm" data-testid="app-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center gap-2">
        {/* Logo */}
        <button data-testid="nav-logo" onClick={() => navigate('/dashboard')} className="flex items-center gap-2 shrink-0">
          <Heart className="w-7 h-7 text-rose-500 fill-rose-500" />
          <span className="text-lg sm:text-xl font-bold text-rose-900 hidden xs:inline sm:inline" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Intentional Connections
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-1">
          {subscription && <div className="mr-1"><PlanBadge planType={subscription.plan_type} /></div>}          {links.map(({ to, label, icon: Icon, badge }) => (
            <Button
              key={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}-btn`}
              variant="ghost"
              size="sm"
              onClick={() => navigate(to)}
              className={`relative ${isActive(to) ? 'bg-rose-50 text-rose-700' : 'text-gray-600 hover:bg-rose-50 hover:text-rose-700'}`}
            >
              <Icon className="w-4 h-4 mr-1.5" />
              {label}
              {badge > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{badge}</span>
              )}
            </Button>
          ))}
          <NotificationBell />
          <Button data-testid="nav-logout-btn" variant="ghost" size="sm" onClick={handleLogout} className="text-gray-500 hover:bg-rose-50 hover:text-rose-700">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>

        {/* Mobile: badge + hamburger */}
        <div className="flex items-center gap-2 lg:hidden">
          {subscription && <PlanBadge planType={subscription.plan_type} />}
          <NotificationBell />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="nav-menu-btn" variant="ghost" size="icon" className="text-rose-700">
                <Menu className="w-6 h-6" />
                {conciergeBadge > 0 && <span className="absolute top-2 right-2 bg-rose-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{conciergeBadge}</span>}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0" data-testid="nav-mobile-menu">
              <SheetHeader className="px-5 py-4 border-b border-rose-100">
                <SheetTitle className="flex items-center gap-2 text-rose-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  <Heart className="w-5 h-5 text-rose-500 fill-rose-500" /> {user?.name || 'Menu'}
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col py-2">
                {links.map(({ to, label, icon: Icon, badge }) => (
                  <button
                    key={to}
                    data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}-btn`}
                    onClick={() => go(to)}
                    className={`flex items-center gap-3 px-5 py-3 text-left transition-colors ${isActive(to) ? 'bg-rose-50 text-rose-700 font-medium' : 'text-gray-700 hover:bg-rose-50'}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1">{label}</span>
                    {badge > 0 && <span className="bg-rose-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{badge}</span>}
                  </button>
                ))}
                <button
                  data-testid="mobile-nav-logout-btn"
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-5 py-3 text-left text-gray-500 hover:bg-rose-50 border-t border-rose-100 mt-2"
                >
                  <LogOut className="w-5 h-5" /> <span>Log out</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default AppNav;
